import { AddressSuggestion } from '../utils/addressSearch';

/**
 * Local address data structure
 */
interface LocalAddress {
  number: string;
  addition: string;
  street: string;
  postcode: string;
  address: string;
  coordinates: [number, number]; // [lng, lat]
}

/**
 * Local Address Search Service
 * Provides fast, offline-capable address search for Osdorp-Midden neighborhood
 */
class LocalAddressSearchService {
  private addresses: LocalAddress[] = [];
  private streetIndex: Map<string, LocalAddress[]> = new Map();
  private initialized: boolean = false;
  private initializing: Promise<void> | null = null;

  /**
   * Initialize the address index from GeoJSON
   * Uses fetch to load the file at runtime (avoids webpack bundling issues with large files)
   */
  private async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      try {
        // Fetch the GeoJSON file from public folder at runtime
        // This avoids webpack bundling issues with large files (1.8MB)
        const response = await fetch('/data/json/neighbourhood_address_4326.geojson');
        if (!response.ok) {
          throw new Error(`Failed to load address data: ${response.status} ${response.statusText}`);
        }
        
        const neighbourhoodAddresses = await response.json();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[LocalSearch] Loaded GeoJSON file');
          console.log('[LocalSearch] Has features?', !!neighbourhoodAddresses.features);
        }

        const features = neighbourhoodAddresses.features || [];
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[LocalSearch] Features array length:', features.length);
        }
        
        this.addresses = features.map((feature: any) => {
          const props = feature.properties;
          const coords = feature.geometry.coordinates; // [lng, lat]
          
          return {
            number: props.number || '',
            addition: props.addition || '',
            street: props.street || '',
            postcode: props.postcode || '',
            address: props.address || '',
            coordinates: coords,
          };
        });

        // Build street name index for fast lookup
        this.addresses.forEach(addr => {
          const streetLower = addr.street.toLowerCase();
          if (!this.streetIndex.has(streetLower)) {
            this.streetIndex.set(streetLower, []);
          }
          this.streetIndex.get(streetLower)!.push(addr);
        });

        this.initialized = true;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[LocalSearch] Initialized with ${this.addresses.length} addresses, ${this.streetIndex.size} unique streets`);
          // Check if Evertsweertstraat is in the index
          const evertsweert = this.streetIndex.get('evertsweertstraat');
          if (evertsweert) {
            console.log(`[LocalSearch] Found Evertsweertstraat with ${evertsweert.length} addresses`);
          } else {
            console.warn(`[LocalSearch] Evertsweertstraat NOT found in index!`);
          }
        }
      } catch (error) {
        console.error('Error initializing local address search:', error);
        this.initialized = false; // Allow retry
        this.initializing = null;
        throw error;
      }
    })();

    return this.initializing;
  }

  /**
   * Search addresses by query string
   * Matches street names (case-insensitive, partial match)
   */
  async search(query: string, limit: number = 5): Promise<AddressSuggestion[]> {
    await this.initialize();

    if (!query || query.trim().length < 2) {
      return [];
    }

    const queryLower = query.trim().toLowerCase();
    const results: AddressSuggestion[] = [];
    const seenStreets = new Set<string>();

    // Debug: Check if index is populated
    if (process.env.NODE_ENV === 'development') {
      console.log(`[LocalSearch] Searching for "${queryLower}", streetIndex size: ${this.streetIndex.size}`);
    }

    // Strategy 1: Exact street name match (highest priority)
    if (this.streetIndex.has(queryLower)) {
      const addresses = this.streetIndex.get(queryLower)!;
      addresses.slice(0, limit).forEach(addr => {
        results.push(this.localAddressToSuggestion(addr));
      });
      if (process.env.NODE_ENV === 'development') {
        console.log(`[LocalSearch] Found ${results.length} exact matches`);
      }
      return results;
    }

    // Strategy 2: Street names that start with query
    const streetEntries = Array.from(this.streetIndex.entries());
    for (const [streetName, addresses] of streetEntries) {
      if (streetName.startsWith(queryLower) && !seenStreets.has(streetName)) {
        seenStreets.add(streetName);
        // Add first address from this street
        results.push(this.localAddressToSuggestion(addresses[0]));
        if (results.length >= limit) break;
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[LocalSearch] After startsWith check: ${results.length} results`);
    }

    // Strategy 3: Street names that contain query (if we still need more results)
    if (results.length < limit) {
      for (const [streetName, addresses] of streetEntries) {
        if (streetName.includes(queryLower) && !seenStreets.has(streetName)) {
          seenStreets.add(streetName);
          results.push(this.localAddressToSuggestion(addresses[0]));
          if (process.env.NODE_ENV === 'development' && results.length <= 3) {
            console.log(`[LocalSearch] Found match via contains: "${streetName}"`);
          }
          if (results.length >= limit) break;
        }
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[LocalSearch] Final results: ${results.length} for "${queryLower}"`);
      if (results.length > 0) {
        console.log(`[LocalSearch] Result streets:`, results.map(r => {
          // Extract street name from display_name (format: "number street, postcode...")
          const parts = r.display_name.split(',');
          return parts[0].trim();
        }));
      }
    }

    return results.slice(0, limit);
  }

  /**
   * Convert local address to AddressSuggestion format (compatible with Nominatim)
   */
  private localAddressToSuggestion(addr: LocalAddress): AddressSuggestion {
    // Clean up addition field (remove "nan" and trim)
    const addition = addr.addition && addr.addition !== 'nan' ? ` ${addr.addition}` : '';
    
    // Format display name similar to Nominatim
    // Format: "number addition street, postcode Amsterdam, Nederland"
    const displayName = `${addr.number}${addition} ${addr.street}, ${addr.postcode} Amsterdam, Nederland`;
    
    return {
      display_name: displayName,
      lat: addr.coordinates[1].toString(), // lat is second element
      lon: addr.coordinates[0].toString(), // lng is first element
      place_id: this.generatePlaceId(addr), // Generate a unique ID
      type: 'house',
      importance: 0.5, // Medium importance for local addresses
    };
  }

  /**
   * Generate a unique place ID from address
   */
  private generatePlaceId(addr: LocalAddress): number {
    // Create a hash-like ID from address components
    const str = `${addr.street}-${addr.number}-${addr.postcode}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Get all unique street names (for autocomplete)
   */
  async getStreetNames(): Promise<string[]> {
    await this.initialize();
    return Array.from(this.streetIndex.keys()).sort();
  }

  /**
   * Get addresses for a specific street
   */
  async getAddressesByStreet(streetName: string): Promise<LocalAddress[]> {
    await this.initialize();
    const streetLower = streetName.toLowerCase();
    return this.streetIndex.get(streetLower) || [];
  }

  /**
   * Get total number of addresses
   */
  async getTotalAddressCount(): Promise<number> {
    await this.initialize();
    return this.addresses.length;
  }
}

export const localAddressSearch = new LocalAddressSearchService();
export type { LocalAddress };
